/**
 * Filtering Utilities
 *
 * Client-side filtering logic for the DataTable component.
 */

import { ColumnDefinition, FilterState } from '../types';
import { getColumnValue } from './sorting';

/**
 * Check if a value matches a filter
 */
function matchesFilter(value: any, filter: FilterState): boolean {
  // Null/undefined handling
  if (value == null) {
    return filter.value == null;
  }

  const filterValue = filter.value;
  const operator = filter.operator || 'eq';

  switch (operator) {
    case 'eq':
      return value === filterValue;

    case 'ne':
      return value !== filterValue;

    case 'gt':
      return value > filterValue;

    case 'gte':
      return value >= filterValue;

    case 'lt':
      return value < filterValue;

    case 'lte':
      return value <= filterValue;

    case 'contains':
      return String(value).toLowerCase().includes(String(filterValue).toLowerCase());

    case 'startsWith':
      return String(value).toLowerCase().startsWith(String(filterValue).toLowerCase());

    case 'endsWith':
      return String(value).toLowerCase().endsWith(String(filterValue).toLowerCase());

    default:
      return true;
  }
}

/**
 * Filter data array by multiple column filters
 */
export function filterData<TData>(
  data: TData[],
  filters: FilterState[],
  columns: ColumnDefinition<TData>[]
): TData[] {
  if (filters.length === 0) {
    return data;
  }

  return data.filter((row) => {
    // Row must match ALL filters (AND logic)
    return filters.every((filter) => {
      const column = columns.find((col) => col.id === filter.columnId);
      if (!column) return true; // Skip unknown columns

      const value = getColumnValue(row, column);
      return matchesFilter(value, filter);
    });
  });
}

/**
 * Check if filters are active
 */
export function hasActiveFilters(filters: FilterState[]): boolean {
  return filters.length > 0;
}

/**
 * Clear filters for a specific column
 */
export function clearColumnFilter(
  filters: FilterState[],
  columnId: string
): FilterState[] {
  return filters.filter((f) => f.columnId !== columnId);
}

/**
 * Update or add a column filter
 */
export function updateColumnFilter(
  filters: FilterState[],
  columnId: string,
  value: any,
  operator?: FilterState['operator']
): FilterState[] {
  const existingIndex = filters.findIndex((f) => f.columnId === columnId);

  const newFilter: FilterState = {
    columnId,
    value,
    operator: operator || 'eq',
  };

  if (existingIndex >= 0) {
    // Update existing filter
    const newFilters = [...filters];
    newFilters[existingIndex] = newFilter;
    return newFilters;
  } else {
    // Add new filter
    return [...filters, newFilter];
  }
}
